import { MAIL_TEMPLATE_KEYS } from './mail-templates';

export type MailTemplateVariableType = 'string' | 'otp' | 'url' | 'multiline';

export type MailTemplateUserRole = 'all' | 'member' | 'creator' | 'admin' | 'system';

export type MailTemplateVariableDef = {
  name: string;
  type: MailTemplateVariableType;
  required: boolean;
  description: string;
};

export type MailTemplateRegistryEntry = {
  key: string;
  name: string;
  description: string;
  category: 'auth' | 'notification' | 'billing' | 'platform' | 'system' | 'custom';
  isSystem: boolean;
  usesLayout: boolean;
  variables: MailTemplateVariableDef[];
  /** Primary audience for this template. */
  userRole: MailTemplateUserRole;
  /** Product area that triggers this email. */
  featureAssociated: string;
  /** Auth templates always allow code fallback even when MAIL_TEMPLATE_STRICT is on. */
  isAuth: boolean;
};

/** Optional personalization fields allowed in any template body/subject. */
export const MAIL_USER_CONTEXT_VARIABLES: MailTemplateVariableDef[] = [
  { name: 'displayName', type: 'string', required: false, description: 'Display name (e.g. Alex)' },
  { name: 'firstName', type: 'string', required: false, description: 'First name' },
  { name: 'fullName', type: 'string', required: false, description: 'Full name' },
  { name: 'email', type: 'string', required: false, description: 'Recipient email address' },
  { name: 'username', type: 'string', required: false, description: '@username handle' },
];

const layoutVars: MailTemplateVariableDef[] = [
  { name: 'title', type: 'string', required: true, description: 'Email heading shown in layout header' },
  { name: 'bodyHtml', type: 'string', required: true, description: 'Inner HTML content (includes CTA when applicable)' },
  { name: 'logoUrl', type: 'url', required: false, description: 'Logo image URL (filled from env at send time)' },
  { name: 'appUrl', type: 'url', required: false, description: 'App home URL (filled from env at send time)' },
];

function withUserContext(vars: MailTemplateVariableDef[]): MailTemplateVariableDef[] {
  const names = new Set(vars.map((v) => v.name));
  const extras = MAIL_USER_CONTEXT_VARIABLES.filter((v) => !names.has(v.name));
  return [...vars, ...extras];
}

export const MAIL_TEMPLATE_REGISTRY: Record<string, MailTemplateRegistryEntry> = {
  [MAIL_TEMPLATE_KEYS.SYSTEM_LAYOUT]: {
    key: MAIL_TEMPLATE_KEYS.SYSTEM_LAYOUT,
    name: 'System layout shell',
    description: 'Branded outer HTML wrapper — logo, header, footer, legal & social links',
    category: 'system',
    isSystem: true,
    usesLayout: false,
    userRole: 'system',
    featureAssociated: 'Email layout',
    isAuth: false,
    variables: layoutVars,
  },
  [MAIL_TEMPLATE_KEYS.AUTH_VERIFY_EMAIL]: {
    key: MAIL_TEMPLATE_KEYS.AUTH_VERIFY_EMAIL,
    name: 'Verify email',
    description: 'OTP sent during signup or login verification',
    category: 'auth',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Signup & login',
    isAuth: true,
    variables: withUserContext([
      { name: 'code', type: 'otp', required: true, description: '6-digit verification code' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.AUTH_PASSWORD_RESET]: {
    key: MAIL_TEMPLATE_KEYS.AUTH_PASSWORD_RESET,
    name: 'Password reset',
    description: 'OTP for password reset',
    category: 'auth',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Account security',
    isAuth: true,
    variables: withUserContext([
      { name: 'code', type: 'otp', required: true, description: '6-digit reset code' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.AUTH_EMAIL_CHANGE]: {
    key: MAIL_TEMPLATE_KEYS.AUTH_EMAIL_CHANGE,
    name: 'Email change confirmation',
    description: 'Confirm a new sign-in email address',
    category: 'auth',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Account settings',
    isAuth: true,
    variables: withUserContext([
      { name: 'newEmail', type: 'string', required: true, description: 'New email address' },
      { name: 'href', type: 'url', required: true, description: 'Confirmation link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.AUTH_SECURITY_ALERT]: {
    key: MAIL_TEMPLATE_KEYS.AUTH_SECURITY_ALERT,
    name: 'Security alert',
    description: 'Account security change notification',
    category: 'auth',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Account security',
    isAuth: true,
    variables: withUserContext([
      { name: 'action', type: 'string', required: true, description: 'What changed (e.g. Password updated)' },
      { name: 'href', type: 'url', required: true, description: 'Security settings link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.AUTH_MEMBER_WELCOME]: {
    key: MAIL_TEMPLATE_KEYS.AUTH_MEMBER_WELCOME,
    name: 'Member welcome',
    description: 'Welcome email after email verification',
    category: 'auth',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Onboarding',
    isAuth: true,
    variables: withUserContext([
      { name: 'feedHref', type: 'url', required: true, description: 'Link to home feed' },
      { name: 'settingsHref', type: 'url', required: true, description: 'Notification settings link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.AUTH_CREATOR_WELCOME]: {
    key: MAIL_TEMPLATE_KEYS.AUTH_CREATOR_WELCOME,
    name: 'Creator welcome',
    description: 'Welcome email when creator status becomes active',
    category: 'auth',
    isSystem: true,
    usesLayout: true,
    userRole: 'creator',
    featureAssociated: 'Creator activation',
    isAuth: true,
    variables: withUserContext([
      { name: 'studioHref', type: 'url', required: true, description: 'Loamify Studio link' },
      { name: 'profileHref', type: 'url', required: true, description: 'Public profile link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.BILLING_RECEIPT]: {
    key: MAIL_TEMPLATE_KEYS.BILLING_RECEIPT,
    name: 'Billing receipt',
    description: 'Subscription or purchase receipt',
    category: 'billing',
    isSystem: true,
    usesLayout: true,
    userRole: 'creator',
    featureAssociated: 'Billing & subscriptions',
    isAuth: false,
    variables: withUserContext([
      { name: 'planName', type: 'string', required: false, description: 'Plan display name' },
      { name: 'amountLabel', type: 'string', required: false, description: 'Formatted amount paid' },
      { name: 'invoiceNumber', type: 'string', required: false, description: 'Stripe invoice number' },
      { name: 'billingInterval', type: 'string', required: false, description: 'Monthly or Annual' },
      { name: 'href', type: 'url', required: false, description: 'Billing settings or invoice link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.NOTIFICATION_MENTION_MISSED]: {
    key: MAIL_TEMPLATE_KEYS.NOTIFICATION_MENTION_MISSED,
    name: 'Missed mention',
    description: 'Email when user missed an @mention',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Social notifications',
    isAuth: false,
    variables: withUserContext([
      { name: 'actorName', type: 'string', required: true, description: 'Who mentioned you' },
      { name: 'href', type: 'url', required: true, description: 'Link to post or comment' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.NOTIFICATION_DM_MISSED]: {
    key: MAIL_TEMPLATE_KEYS.NOTIFICATION_DM_MISSED,
    name: 'Missed direct message',
    description: 'Email when user missed a DM',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Direct messages',
    isAuth: false,
    variables: withUserContext([
      { name: 'senderName', type: 'string', required: true, description: 'Message sender display name' },
      { name: 'preview', type: 'string', required: true, description: 'Short message preview' },
      { name: 'href', type: 'url', required: true, description: 'Conversation link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.NOTIFICATION_DAILY_DIGEST]: {
    key: MAIL_TEMPLATE_KEYS.NOTIFICATION_DAILY_DIGEST,
    name: 'Daily digest',
    description: 'Daily activity summary email',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Notification digest',
    isAuth: false,
    variables: withUserContext([
      { name: 'digestLines', type: 'multiline', required: true, description: 'Auto-generated activity lines (one per line)' },
      { name: 'notificationsHref', type: 'url', required: true, description: 'Notifications page URL' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.NOTIFICATION_WEEKLY_DIGEST]: {
    key: MAIL_TEMPLATE_KEYS.NOTIFICATION_WEEKLY_DIGEST,
    name: 'Weekly digest',
    description: 'Weekly activity summary email',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Notification digest',
    isAuth: false,
    variables: withUserContext([
      { name: 'digestLines', type: 'multiline', required: true, description: 'Auto-generated activity lines (one per line)' },
      { name: 'notificationsHref', type: 'url', required: true, description: 'Notifications page URL' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.NOTIFICATION_IMMEDIATE]: {
    key: MAIL_TEMPLATE_KEYS.NOTIFICATION_IMMEDIATE,
    name: 'Immediate notification',
    description: 'Instant notification email',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Social notifications',
    isAuth: false,
    variables: withUserContext([
      { name: 'summary', type: 'string', required: true, description: 'Short notification summary (auto-generated)' },
      { name: 'href', type: 'url', required: true, description: 'Deep link to the activity' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.PLATFORM_ANNOUNCEMENT]: {
    key: MAIL_TEMPLATE_KEYS.PLATFORM_ANNOUNCEMENT,
    name: 'Platform announcement',
    description: 'Admin broadcast to members — compose fills the message body',
    category: 'platform',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Platform broadcast',
    isAuth: false,
    variables: withUserContext([
      { name: 'title', type: 'string', required: true, description: 'Announcement headline (set at compose time)' },
      { name: 'body', type: 'multiline', required: true, description: 'Main announcement paragraph (set at compose time)' },
      { name: 'ctaLabel', type: 'string', required: false, description: 'Optional CTA button label' },
      { name: 'ctaHref', type: 'url', required: false, description: 'Optional CTA button URL' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.COMMUNITY_BROADCAST]: {
    key: MAIL_TEMPLATE_KEYS.COMMUNITY_BROADCAST,
    name: 'Circle broadcast',
    description: 'Creator broadcast to Circle members',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Circle broadcasts',
    isAuth: false,
    variables: withUserContext([
      { name: 'communityName', type: 'string', required: true, description: 'Circle name' },
      { name: 'title', type: 'string', required: true, description: 'Broadcast subject line' },
      { name: 'body', type: 'multiline', required: true, description: 'Broadcast message body' },
      { name: 'ctaLabel', type: 'string', required: false, description: 'Optional CTA label' },
      { name: 'ctaHref', type: 'url', required: false, description: 'Circle landing URL' },
      {
        name: 'discussionCtaLabel',
        type: 'string',
        required: false,
        description: 'Secondary reader discussion CTA label (S23 Lite)',
      },
      {
        name: 'discussionCtaHref',
        type: 'url',
        required: false,
        description: 'Circle home URL for reader discussion',
      },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.COURSE_PURCHASE_RECEIPT]: {
    key: MAIL_TEMPLATE_KEYS.COURSE_PURCHASE_RECEIPT,
    name: 'Course purchase receipt',
    description: 'Receipt after a paid course purchase',
    category: 'billing',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Courses',
    isAuth: false,
    variables: withUserContext([
      { name: 'courseTitle', type: 'string', required: true, description: 'Course title' },
      { name: 'amountLabel', type: 'string', required: false, description: 'Formatted purchase amount' },
      { name: 'receiptUrl', type: 'url', required: false, description: 'Link to course or receipt' },
      { name: 'href', type: 'url', required: true, description: 'Primary CTA link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.COURSE_ENROLLED]: {
    key: MAIL_TEMPLATE_KEYS.COURSE_ENROLLED,
    name: 'Course enrolled',
    description: 'Welcome email when a learner enrolls in a course',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Courses',
    isAuth: false,
    variables: withUserContext([
      { name: 'courseTitle', type: 'string', required: true, description: 'Course title' },
      { name: 'href', type: 'url', required: true, description: 'Link to start learning' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.COURSE_DRIP_UNLOCK]: {
    key: MAIL_TEMPLATE_KEYS.COURSE_DRIP_UNLOCK,
    name: 'Course drip unlock',
    description: 'New module unlocked via drip schedule',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Courses',
    isAuth: false,
    variables: withUserContext([
      { name: 'courseTitle', type: 'string', required: true, description: 'Course title' },
      { name: 'moduleTitle', type: 'string', required: true, description: 'Unlocked module title' },
      { name: 'href', type: 'url', required: true, description: 'Link to new lessons' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.COURSE_CERTIFICATE_READY]: {
    key: MAIL_TEMPLATE_KEYS.COURSE_CERTIFICATE_READY,
    name: 'Course certificate ready',
    description: 'Certificate available for download',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Courses',
    isAuth: false,
    variables: withUserContext([
      { name: 'courseTitle', type: 'string', required: true, description: 'Course title' },
      { name: 'certificateCode', type: 'string', required: false, description: 'Certificate reference code' },
      { name: 'href', type: 'url', required: true, description: 'Link to download certificate' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.MEMBERSHIP_PERK_DELIVERED]: {
    key: MAIL_TEMPLATE_KEYS.MEMBERSHIP_PERK_DELIVERED,
    name: 'Membership perk delivered',
    description: 'Access link for a membership perk',
    category: 'billing',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Creator memberships',
    isAuth: false,
    variables: withUserContext([
      { name: 'membershipTitle', type: 'string', required: true, description: 'Membership product name' },
      { name: 'perkTitle', type: 'string', required: true, description: 'Perk title' },
      { name: 'accessUrl', type: 'url', required: true, description: 'Perk access link' },
      { name: 'href', type: 'url', required: true, description: 'Membership page link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.MEMBERSHIP_SUBSCRIPTION_RECEIPT]: {
    key: MAIL_TEMPLATE_KEYS.MEMBERSHIP_SUBSCRIPTION_RECEIPT,
    name: 'Membership subscription receipt',
    description: 'Receipt after subscribing to a creator membership',
    category: 'billing',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Creator memberships',
    isAuth: false,
    variables: withUserContext([
      { name: 'membershipTitle', type: 'string', required: true, description: 'Membership product name' },
      { name: 'tierName', type: 'string', required: true, description: 'Tier name' },
      { name: 'amountLabel', type: 'string', required: false, description: 'Formatted subscription amount' },
      { name: 'href', type: 'url', required: true, description: 'Membership page link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.MEMBERSHIP_WELCOME]: {
    key: MAIL_TEMPLATE_KEYS.MEMBERSHIP_WELCOME,
    name: 'Membership welcome',
    description: 'Welcome email when joining a creator membership',
    category: 'notification',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Creator memberships',
    isAuth: false,
    variables: withUserContext([
      { name: 'membershipTitle', type: 'string', required: true, description: 'Membership product name' },
      { name: 'tierName', type: 'string', required: true, description: 'Tier name' },
      { name: 'creatorName', type: 'string', required: false, description: 'Creator display name' },
      { name: 'href', type: 'url', required: true, description: 'Membership page link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.MEMBERSHIP_BILLING_PAST_DUE]: {
    key: MAIL_TEMPLATE_KEYS.MEMBERSHIP_BILLING_PAST_DUE,
    name: 'Membership billing past due',
    description: 'Payment failed — update billing to keep access',
    category: 'billing',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Creator memberships',
    isAuth: false,
    variables: withUserContext([
      { name: 'membershipTitle', type: 'string', required: true, description: 'Membership product name' },
      { name: 'tierName', type: 'string', required: true, description: 'Tier name' },
      { name: 'href', type: 'url', required: true, description: 'Update payment link' },
    ]),
  },
  [MAIL_TEMPLATE_KEYS.MEMBERSHIP_CANCELLED]: {
    key: MAIL_TEMPLATE_KEYS.MEMBERSHIP_CANCELLED,
    name: 'Membership cancelled',
    description: 'Membership access ended or subscription cancelled',
    category: 'billing',
    isSystem: true,
    usesLayout: true,
    userRole: 'member',
    featureAssociated: 'Creator memberships',
    isAuth: false,
    variables: withUserContext([
      { name: 'membershipTitle', type: 'string', required: true, description: 'Membership product name' },
      { name: 'tierName', type: 'string', required: false, description: 'Tier name' },
      { name: 'href', type: 'url', required: false, description: 'Rejoin or browse link' },
    ]),
  },
};

export function getRegistryEntry(key: string): MailTemplateRegistryEntry | undefined {
  return MAIL_TEMPLATE_REGISTRY[key];
}

export function isAuthTemplateKey(key: string): boolean {
  return MAIL_TEMPLATE_REGISTRY[key]?.isAuth === true;
}

export function listSystemTemplateKeys(): string[] {
  return Object.values(MAIL_TEMPLATE_REGISTRY)
    .filter((e) => e.isSystem)
    .map((e) => e.key);
}

export function validateCustomTemplateKey(key: string): boolean {
  return /^custom\.[a-z][a-z0-9._]{2,62}$/.test(key);
}

export const MAIL_TEMPLATE_CATEGORIES = [
  { id: 'auth', label: 'Authentication', description: 'Signup, login, security' },
  { id: 'notification', label: 'Notifications', description: 'Activity alerts & digests' },
  { id: 'billing', label: 'Billing', description: 'Receipts & payments' },
  { id: 'platform', label: 'Platform', description: 'Admin broadcasts' },
  { id: 'system', label: 'System', description: 'Layout & infrastructure' },
  { id: 'custom', label: 'Custom', description: 'Admin-created templates' },
] as const;

export function allowedVariableNames(key: string): Set<string> {
  const entry = getRegistryEntry(key);
  const names = new Set(entry?.variables.map((v) => v.name) ?? []);
  for (const v of MAIL_USER_CONTEXT_VARIABLES) {
    names.add(v.name);
  }
  return names;
}
